import React, { useState, useEffect } from "react";
import axios from "axios";
import { openDB } from 'idb'; // Ensure idb is installed with `npm install idb`

const InspectorHub = () => {
  const [activeTab, setActiveTab] = useState("image");
  const [selectedFile, setSelectedFile] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("IN");
  const [language, setLanguage] = useState("hi");
  const [isActive, setIsActive] = useState(true);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewURL, setPreviewURL] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL

  // Sample country options
  const countries = [
    { value: "IN", label: "India (IN)" },
    { value: "US", label: "United States (US)" },
    { value: "GB", label: "United Kingdom (GB)" },
    { value: "CA", label: "Canada (CA)" },
    { value: "AU", label: "Australia (AU)" },
    { value: "FR", label: "France (FR)" },
    { value: "DE", label: "Germany (DE)" },
  ];

  // Sample language options
  const languages = [
    { value: "hi", label: "Hindi (hi)" },
    { value: "en", label: "English (en)" },
    { value: "fr", label: "French (fr)" },
    { value: "es", label: "Spanish (es)" },
    { value: "de", label: "German (de)" },
    { value: "ja", label: "Japanese (ja)" },
  ];

  // Initialize IndexedDB
  const initDB = async () => {
    return openDB('InspectorHubDB', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'tab' });
        }
        if (!db.objectStoreNames.contains('responses')) {
          db.createObjectStore('responses', { keyPath: 'tab' });
        }
      },
    });
  };

  // Load cached data from IndexedDB
  useEffect(() => {
    const loadCachedData = async () => {
      const db = await initDB();
      const transaction = db.transaction(['files', 'responses'], 'readonly');
      const filesStore = transaction.objectStore('files');
      const responsesStore = transaction.objectStore('responses');

      const [cachedFile, cachedResponse] = await Promise.all([
        filesStore.get(activeTab),
        responsesStore.get(activeTab)
      ]);

      if (cachedFile) {
        setSelectedFile(new File([new Blob([new Uint8Array(cachedFile.data)])], cachedFile.name, { type: cachedFile.type }));
        setPreviewURL(URL.createObjectURL(new Blob([new Uint8Array(cachedFile.data)], { type: cachedFile.type })));
      } else {
        setSelectedFile(null);
        setPreviewURL(null);
      }

      if (cachedResponse) {
        setResponse(cachedResponse.data);
      } else {
        setResponse(null);
      }

      await transaction.done;
      db.close();
    };
    loadCachedData();
  }, [activeTab]);

  // handle tab switch
  const handleTabChange = (tab) => {
    if (loading) return;
    setActiveTab(tab);
  };

  // file selection
  const handleFileChange = async (e) => {
    if (loading) return;
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const fileData = {
          tab: activeTab,
          name: file.name,
          type: file.type,
          data: new Uint8Array(reader.result)
        };
        setSelectedFile(file);
        setPreviewURL(URL.createObjectURL(file));
        const db = await initDB();
        const transaction = db.transaction('files', 'readwrite');
        await transaction.objectStore('files').put(fileData);
        await transaction.done;
        db.close();
      };
      reader.readAsArrayBuffer(file);
    } else {
      setSelectedFile(null);
      setPreviewURL(null);
      const db = await initDB();
      const transaction = db.transaction('files', 'readwrite');
      await transaction.objectStore('files').delete(activeTab);
      await transaction.done;
      db.close();
    }
  };

  // main upload handler
  const handleUpload = async () => {
    try {
      setLoading(true);
      setResponse(null);

      if (activeTab === "keyword") {
        if (!keyword.trim()) {
          alert("Please enter a keyword to search.");
          setLoading(false);
          return;
        }

        const payload = {
          keywords: keyword,
          location_code: country,
          language_code: language,
          is_user_registered: isActive ? "True" : "False"
        };

        const res = await axios.post(`${BASE_URL}/frame_iq/api/inspect/keyword`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        setResponse(res.data);
        const db = await initDB();
        const transaction = db.transaction('responses', 'readwrite');
        await transaction.objectStore('responses').put({ tab: activeTab, data: res.data });
        await transaction.done;
        db.close();
      } else {
        if (!selectedFile) {
          alert("Please select a file to upload.");
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append("inspector_type", activeTab);
        formData.append("file", selectedFile);

        const res = await axios.post(`${BASE_URL}/frame_iq/api/inspect/media`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setResponse(res.data?.body || res.data);
        const db = await initDB();
        const transaction = db.transaction('responses', 'readwrite');
        await transaction.objectStore('responses').put({ tab: activeTab, data: res.data?.body || res.data });
        await transaction.done;
        db.close();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setResponse(error.response?.data || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // preview display
  const renderPreview = () => {
    if (!previewURL) return null;
    if (activeTab === "image")
      return <img src={previewURL} alt="Preview" style={styles.mediaPreview} />;
    if (activeTab === "video")
      return <video src={previewURL} controls style={styles.mediaPreview} />;
    //if (activeTab === "audio")
      //return <audio src={previewURL} controls style={{ width: "100%" }} />;
    return null;
  };

  // recursive HTML response
  const renderHtmlResponse = (data) => {
    if (!data || typeof data !== "object") return null;

    return Object.entries(data).map(([key, value], index) => (
      <div key={index} style={styles.responseRow}>
        <div style={styles.responseKey}>{key}:</div>
        <div style={styles.responseValue}>
          {typeof value === "object" && value !== null ? (
            <div style={{ marginLeft: "15px" }}>{renderHtmlResponse(value)}</div>
          ) : key === "csv_url" ? (
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          ) : (
            <span>{String(value)}</span>
          )}
        </div>
      </div>
    ));
  };

  // Render keyword table
  const renderKeywordTable = () => {
    if (!response || !response.external_response || !Array.isArray(response.external_response)) {
      return <p>No results found.</p>;
    }

    const data = response.external_response;

    return (
      <div>
        <h4>Results: {data.length} videos</h4>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Keyword</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>URL</th>
              <th style={styles.th}>Owner</th>
              <th style={styles.th}>Views</th>
              <th style={styles.th}>Length (Seconds)</th>
              <th style={styles.th}>Upload Date</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} style={styles.tr}>
                <td style={styles.td}>{item.keyword || 'N/A'}</td>
                <td style={styles.td}>{item.category || 'N/A'}</td>
                <td style={styles.td}>{item.title || 'N/A'}</td>
                <td style={styles.td}>
                  <a href={item.video_url || '#'} target="_blank" rel="noopener noreferrer">
                    {item.video_url || 'N/A'}
                  </a>
                </td>
                <td style={styles.td}>{item.owner_channel_name || 'N/A'}</td>
                <td style={styles.td}>{item.view_count || 'N/A'}</td>
                <td style={styles.td}>{item.length_seconds || 'N/A'}</td>
                <td style={styles.td}>{item.publish_date || 'N/A'}</td>
                <td style={styles.td}>{item.status || 'Active'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {response.csv_url && (
          <div style={styles.downloadLink}>
            <a href={response.csv_url} download="keyword_results.csv">
              Download CSV
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h1>FrameIQ</h1>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        {["image", "video", "keyword"].map((tab) => (
          <button
            key={tab}
            disabled={loading}
            style={{
              ...styles.tabButton,
              backgroundColor: activeTab === tab ? "#4b6cb7" : "#e0e0e0",
              color: activeTab === tab ? "#fff" : "#000",
              opacity: loading ? 0.5 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={() => handleTabChange(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Upload/Input Section */}
      <div style={styles.uploadSection}>
        {activeTab === "keyword" ? (
          <div style={styles.keywordSection}>
            <h2>Keyword Search</h2>

            <input
              type="text"
              value={keyword}
              onChange={(e) => !loading && setKeyword(e.target.value)}
              placeholder="Keyword (e.g., Modi)"
              style={styles.keywordInput}
              disabled={loading}
            />

            <div style={styles.keywordFields}>
              <select
                value={country}
                onChange={(e) => !loading && setCountry(e.target.value)}
                style={styles.smallInput}
                disabled={loading}
              >
                {countries.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={language}
                onChange={(e) => !loading && setLanguage(e.target.value)}
                style={styles.smallInput}
                disabled={loading}
              >
                {languages.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={isActive ? "true" : "false"}
                onChange={(e) => !loading && setIsActive(e.target.value === "true")}
                style={styles.smallInput}
                disabled={loading}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
          </div>
        ) : (
          <input type="file" onChange={handleFileChange} disabled={loading} />
        )}

        <button
          onClick={handleUpload}
          disabled={loading}
          style={styles.uploadButton}
        >
          {loading ? "Processing..." : "Submit"}
        </button>
      </div>

      {/* Loader */}
      {loading && (
        <div style={styles.loadingBox}>
          <p>Processing... please wait ⏳</p>
        </div>
      )}

      {/* Response */}
      {response && (
        <div style={activeTab === "keyword" ? styles.fullWidthResponse : styles.resultContainer}>
          {activeTab !== "keyword" && (
            <>
              <div style={styles.leftCol}>{renderPreview()}</div>
              <div style={styles.rightCol}>
                <h4>Response:</h4>
                <div style={styles.htmlResponseBox}>{renderHtmlResponse(response)}</div>
              </div>
            </>
          )}
          {activeTab === "keyword" && (
            <div style={styles.fullWidthResponse}>
              <h4>Response:</h4>
              {renderKeywordTable()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// -------------------- Styles --------------------
const styles = {
  container: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  tabContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
  },
  tabButton: {
    padding: "15px 40px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "bold",
    transition: "0.3s",
  },
  uploadSection: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  keywordSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flex: 1,
  },
  keywordInput: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    width: "100%",
  },
  keywordFields: {
    display: "flex",
    gap: "10px",
  },
  smallInput: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    flex: "1",
  },
  uploadButton: {
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "12px 20px",
    cursor: "pointer",
  },
  loadingBox: {
    textAlign: "center",
    padding: "10px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  resultContainer: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
  },
  leftCol: {
    flex: "1 1 45%",
    backgroundColor: "#f1f1f1",
    padding: "10px",
    borderRadius: "8px",
  },
  rightCol: {
    flex: "1 1 50%",
    backgroundColor: "#f8f8f8",
    padding: "10px",
    borderRadius: "8px",
    maxHeight: "800px",
    overflowY: "auto",
  },
  mediaPreview: {
    width: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    objectFit: "contain",
  },
  htmlResponseBox: {
    backgroundColor: "#fff",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    marginTop: "10px",
  },
  responseRow: {
    marginBottom: "8px",
  },
  responseKey: {
    fontWeight: "bold",
    color: "#2a4d8f",
  },
  responseValue: {
    color: "#333",
    marginLeft: "8px",
  },
  fullWidthResponse: {
    width: "100%",
    backgroundColor: "#f8f8f8",
    padding: "10px",
    borderRadius: "8px",
    maxHeight: "800px",
    overflowY: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px",
    backgroundColor: "#f0f0f0",
  },
  th: {
    backgroundColor: "#ddd",
    padding: "8px",
    textAlign: "left",
    borderBottom: "1px solid #ccc",
  },
  tr: {
    borderBottom: "1px solid #eee",
  },
  td: {
    padding: "8px",
    textAlign: "left",
  },
  downloadLink: {
    textAlign: "center",
    marginTop: "10px",
  },
};

export default InspectorHub;